import { Button, Modal, Typography, Upload, message } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useRef, useState } from "react";

import styles from "../../WorkOrder.module.scss";

interface PickedFile {
  key: string;
  name: string;
  type: string;
  file: File;
}

/** A document the work order already holds. */
export interface SavedDocument {
  key: string;
  name: string;
  url: string;
  documentId?: string | number;
}

export interface EditDocumentSubmitPayload {
  files: File[];
  deletedDocIds: string[];
  remainingDocs: SavedDocument[];
  isFirstTime: boolean;
}

interface Props {
  open: boolean;
  onCancel: () => void;
  /** The picked files, deleted documents, and remaining documents. */
  onSubmit: (payload: EditDocumentSubmitPayload) => void;
  isSubmitting?: boolean;
  /** The work order's current documents, listed above the new ones. */
  saved?: SavedDocument[];
  /** Opens a saved document, or a picked one (as a File). */
  onView: (document: SavedDocument | File) => void;
  isReturningFromPreview?: boolean;
}

const MAX_FILE_SIZE_MB = 20;
const MAX_FILES = 10;

/**
 * "Edit Document" — drag-and-drop uploader for a document version.
 *
 * Limits (PDF only, 20MB, 10 files) are enforced in `beforeUpload` as well as
 * being stated in the dropzone copy, so a drag-and-drop that bypasses the
 * picker is still rejected. The files and removals are held here and handed to
 * `onSubmit`, which uploads or updates/deletes them; the list empties whenever
 * the modal closes, so a reopened modal starts clean.
 */
export const EditDocumentModal = ({
  open,
  onCancel,
  onSubmit,
  isSubmitting,
  saved = [],
  onView,
  isReturningFromPreview = false,
}: Props) => {
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [remainingSaved, setRemainingSaved] = useState<SavedDocument[]>(saved);
  const [deletedDocIds, setDeletedDocIds] = useState<string[]>([]);
  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (open && !prevOpenRef.current && !isReturningFromPreview) {
      setFiles([]);
      setRemainingSaved(saved);
      setDeletedDocIds([]);
    }
    prevOpenRef.current = open;
  }, [open, saved, isReturningFromPreview]);

  const beforeUpload = (file: File) => {
    if (file.type !== "application/pdf") {
      message.error("Only PDF files are allowed");
      return Upload.LIST_IGNORE;
    }
    if (file.size / 1024 / 1024 > MAX_FILE_SIZE_MB) {
      message.error(`Maximum file size is ${MAX_FILE_SIZE_MB} MB`);
      return Upload.LIST_IGNORE;
    }
    if (remainingSaved.length + files.length >= MAX_FILES) {
      message.error(`Maximum ${MAX_FILES} files`);
      return Upload.LIST_IGNORE;
    }
    setFiles((current) => [
      ...current,
      { key: `${file.name}-${current.length}-${Date.now()}`, name: file.name, type: "pdf", file },
    ]);
    return Upload.LIST_IGNORE; // held locally; no auto-upload
  };

  const handleRemoveSaved = (doc: SavedDocument) => {
    setRemainingSaved((prev) => prev.filter((d) => d.key !== doc.key));
    const docId = String(doc.documentId ?? doc.key);
    setDeletedDocIds((prev) => (prev.includes(docId) ? prev : [...prev, docId]));
  };

  const handleRemovePicked = (fileKey: string) => {
    setFiles((prev) => prev.filter((f) => f.key !== fileKey));
  };

  const isFirstTime = saved.length === 0;
  const hasChanges = files.length > 0 || deletedDocIds.length > 0;
  const hasRemainingOrNew = remainingSaved.length > 0 || files.length > 0;
  const isSubmitDisabled = isFirstTime ? !files.length : !hasChanges || !hasRemainingOrNew;

  return (
    <Modal
      title="Edit Document"
      open={open}
      centered
      maskClosable={false}
      width={560}
      onCancel={onCancel}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            loading={isSubmitting}
            disabled={isSubmitDisabled}
            onClick={() =>
              onSubmit({
                files: files.map((picked) => picked.file),
                deletedDocIds,
                remainingDocs: remainingSaved,
                isFirstTime,
              })
            }
          >
            Submit
          </Button>
        </div>
      }
    >
      <div className={styles.fileRow}>
        <Icon
          icon="vscode-icons:file-type-pdf2"
          className={styles.fileIcon}
        />
        <Typography.Text>Please edit your files</Typography.Text>
      </div>

      <Upload.Dragger
        multiple
        accept="application/pdf"
        beforeUpload={beforeUpload}
        showUploadList={false}
      >
        <p style={{ fontSize: 32, margin: 0 }}>
          <Icon icon="mdi:cloud-upload-outline" />
        </p>
        <Typography.Paragraph style={{ marginBottom: 4 }}>
          Only PDF files are allowed
        </Typography.Paragraph>
        <Typography.Text type="danger">
          (Maximum file Size : {MAX_FILE_SIZE_MB} MB )
        </Typography.Text>
        <br />
        <Typography.Text type="danger">( Maximum Files: {MAX_FILES} )</Typography.Text>
        <Typography.Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
          <a>Click</a> or <a>drag and drop</a> file to upload
        </Typography.Paragraph>
      </Upload.Dragger>

      <div style={{ marginTop: 16 }}>
        {/* Existing documents already in the work order — can be previewed or removed */}
        {remainingSaved.map((document) => (
          <div
            className={styles.fileRow}
            key={document.key}
          >
            <Icon
              icon="vscode-icons:file-type-pdf2"
              className={styles.fileIcon}
            />
            <Typography.Text style={{ flex: 1 }}>{document.name}</Typography.Text>
            <Icon
              icon="akar-icons:eye"
              role="button"
              aria-label={`View ${document.name}`}
              className={styles.viewIcon}
              onClick={() => onView(document)}
            />
            <Icon
              icon="fluent:delete-48-regular"
              role="button"
              aria-label={`Remove ${document.name}`}
              className={styles.viewIcon}
              onClick={() => handleRemoveSaved(document)}
            />
          </div>
        ))}
        {files.map((file) => (
          <div
            className={styles.fileRow}
            key={file.key}
          >
            <Icon
              icon={file.type === "pdf" ? "vscode-icons:file-type-pdf2" : "vscode-icons:file-type-image"}
              className={styles.fileIcon}
            />
            <Typography.Text style={{ flex: 1 }}>{file.name}</Typography.Text>
            <Icon
              icon="akar-icons:eye"
              role="button"
              aria-label={`View ${file.name}`}
              className={styles.viewIcon}
              onClick={() => onView(file.file)}
            />
            <Icon
              icon="fluent:delete-48-regular"
              role="button"
              aria-label={`Remove ${file.name}`}
              className={styles.viewIcon}
              onClick={() => handleRemovePicked(file.key)}
            />
          </div>
        ))}
      </div>
    </Modal>
  );
};
